import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoaderIcon, CreditCard, ShieldCheck, CheckCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { stripeConfig } from "@/config/billing";
import BillingAPIService from "@/services/billing-api";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCountry } from "@/contexts/CountryContext";

const stripePromise = loadStripe(stripeConfig.publicKey);

interface OneTimePaymentProps {
  planName: string;
  amount: number;
  discountedAmount?: number;
  billingTierId: number;
  discountCode?: string;
  onPaymentSuccess?: () => void;
  onPaymentError?: (error: string) => void;
}

const OneTimePaymentForm: React.FC<
  OneTimePaymentProps & { clientSecret: string }
> = ({
  planName,
  amount,
  discountedAmount,
  billingTierId,
  discountCode,
  clientSecret,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { formatCurrency } = useCountry();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const finalAmount = discountedAmount || amount;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!stripe || !elements) {
      return;
    }

    try {
      setIsLoading(true);
      setError(undefined);

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/contractor/profile?payment=success`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.paymentIntent?.status === "succeeded" && onPaymentSuccess) {
        toast({
          title: "Payment Successful!",
          description:
            "Your entity setup payment has been processed successfully.",
        });
        onPaymentSuccess();
      } else if (result.paymentIntent?.status === "succeeded") {
        toast({
          title: "Payment Successful!",
          description:
            "Your entity setup payment has been processed successfully.",
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Payment processing failed";
      setError(message);
      toast({
        title: "Payment Error",
        description: message,
        variant: "destructive",
      });

      if (onPaymentError) {
        onPaymentError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-2">Complete Your Payment</h2>
        <p className="text-muted-foreground">
          Secure payment for your contractor entity setup
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Payment Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span>{planName}</span>
              <span>{formatCurrency(amount)}</span>
            </div>

            {discountCode && discountedAmount && (
              <>
                <div className="flex justify-between text-green-600">
                  <span>Discount ({discountCode})</span>
                  <span>-{formatCurrency(amount - discountedAmount)}</span>
                </div>
                <hr />
              </>
            )}

            <div className="flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span>{formatCurrency(finalAmount)}</span>
            </div>

            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4" />
                <span>Secure payment powered by Stripe</span>
              </div>
              <div className="text-sm text-muted-foreground">
                One-time payment • No recurring charges
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Form */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <PaymentElement />

              <Button
                type="submit"
                className="w-full"
                disabled={!stripe || !elements || isLoading}
              >
                {isLoading ? (
                  <>
                    <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  `Pay ${formatCurrency(finalAmount)}`
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const OneTimePayment: React.FC<OneTimePaymentProps> = (props) => {
  const [clientSecret, setClientSecret] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        setIsLoading(true);

        // Create one-time payment
        const result = await BillingAPIService.createOneTimePayment({
          billingTierId: props.billingTierId,
          discountCode: props.discountCode,
          metadata: {
            plan_name: props.planName,
          },
        });

        // Use the client secret directly from the API response
        if (result.payment.client_secret) {
          setClientSecret(result.payment.client_secret);
        } else {
          throw new Error("Failed to get payment intent client secret");
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to initialize payment";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    createPaymentIntent();
  }, [props.billingTierId, props.discountCode, props.planName]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center">
          <Skeleton className="h-8 w-64 mx-auto mb-2" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!clientSecret) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-6">
          <Alert>
            <AlertDescription>
              Unable to initialize payment. Please try again.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const options = {
    clientSecret,
    appearance: stripeConfig.options.appearance,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <OneTimePaymentForm {...props} clientSecret={clientSecret} />
    </Elements>
  );
};
